import React, {Component, MouseEvent, ReactNode} from 'react';
import I18n from 'app/i18n';
import {inject, observer} from 'mobx-react';
import AsideItem from 'site_version/com/AsideItem';
import {isGuest} from 'app/utils';
import {GiveawayStore} from 'app/store/GiveawayStore';
import Prize from 'app/components/my_profile/artifacts/popup/prize';
import {openRegistration} from 'app/components/auth/auth';
import Timer from 'app/components/reusable/Timer';
import {isoToUnix} from 'app/utils/time/IsoToUnix';
import AccordionItem from 'app/components/giveaway/AccordionItem';
import {prizeName} from 'app/components/my_profile/artifacts/popup/PrizeName';
import ButtonBellWithMessage from 'app/components/reusable/ButtonBellWithMessage';
import {NotificationSettingsStore} from 'app/store/NotificationSettingsStore';
import {toastError, toastSuccess} from 'app/components/toasts/liteToast';
import {UserStore} from 'app/store/UserStore';
import {userStore} from 'app/store/global';
import SpriteIcon from 'app/utils/sprites/SpriteIcon';

interface AsideGiveawayProps {
    giveawayStore?: GiveawayStore
    itemClassName: string
    notificationSettingsStore?: NotificationSettingsStore
    userStore?: UserStore
}

@inject('giveawayStore', 'userStore', 'notificationSettingsStore')
@observer
export default class Giveaway extends Component<AsideGiveawayProps> {
    state = {
        disabled: false,
        showWinners: false
    };

    header = (): ReactNode => {
        const giveawayData = this.props.giveawayStore.giveaway;

        if (!giveawayData) {
            return null;
        }

        const {giveaway: {finished_at, participants_count}} = giveawayData;
        const {notificationSettings} = this.props.notificationSettingsStore;
        const giveaway_notification = notificationSettings.giveaway;
        return <div className="aside-item-heading__title">
            <h3>{I18n.t('giveaway')}</h3>
            <div className="number-participants">
                <ButtonBellWithMessage
                    isActive={giveaway_notification}
                    message={I18n.t('giveaway_subscribe_message')}
                    handleOnClick={this.subscribeNotification}
                />
                <span className="timer"><Timer endTime={isoToUnix(finished_at)} onlyMinutes/></span>
                <span className="participants">
                    ({I18n.t('participants_count', {count: participants_count})})
                </span>
            </div>
        </div>;
    };

    takePart = (e: MouseEvent): void => {
        if (this.state.disabled) {
            return;
        }
        e.preventDefault();
        if (isGuest()) {
            openRegistration('GIVEAWAY_TAKE_PART', null, 'login');
        } else {
            this.props.giveawayStore.takePart().finally(() => this.setState({disabled: false}));
        }
    };

    showHideWinners = (): void => this.setState({showWinners: !this.state.showWinners});

    subscribeNotification = (): void => {
        const {notificationSettings} = this.props.notificationSettingsStore;
        this.props.notificationSettingsStore
            .saveSettings({giveaway: !notificationSettings.giveaway})
            .then(data => {
                if (data) {
                    if (notificationSettings.giveaway) {
                        toastSuccess(I18n.t('giveaway_subscribe_message'));
                    }
                } else {
                    toastError(I18n.t('server_error_try_later'));
                }
            });
    };

    render(): ReactNode {
        const giveawayData = this.props.giveawayStore.giveaway;

        if (!giveawayData || !giveawayData.giveaway) {
            return null;
        }

        const {giveaway, participated, previous_winners} = giveawayData;
        const {showWinners} = this.state;
        const {giveaway_turnover} = this.props.userStore.user;
        const btnText = giveaway_turnover ? I18n.t('prizes_texts.take_part') : I18n.t('prizes_texts.bet_1_dollar_to_participate');
        const btnStyle = `button-adaptive${
            participated ? ' button-adaptive_disable' : ''
        }${
            giveaway_turnover ? '' : ' button-adaptive_inactive'
        }`;

        const {prize} = giveaway;

        return !userStore.user.bonuses_disabled && <AsideItem header={this.header()} name="giveaway" itemClassName={this.props.itemClassName}>
            <div className="giveaway-content">
                <div className="giveaway-prize">
                    <div className="giveaway-prize__image">
                        <Prize loot={prize}/>
                    </div>
                    <div className="giveaway-prize__description">
                        <span className="name">{prizeName(prize)}</span>
                    </div>
                </div>
                <button
                    className={btnStyle}
                    disabled={participated}
                    onClick={this.takePart}
                >
                    {btnText}
                </button>
            </div>
            {previous_winners.length > 0 && <div className="accordion-content">
                <div className="accordion-header">
                    <button className={`button-accordion${showWinners ? ' active' : ''}`}
                        onClick={this.showHideWinners}>
                        <span className="icon">
                            <SpriteIcon name="#svg-arr-select-small" className="svg-default"/>
                        </span>
                        <span className="name">{I18n.t('last_winners')}</span>
                        <span className="icon">
                            <SpriteIcon name="#svg-arr-select-small" className="svg-default"/>
                        </span>
                    </button>
                </div>
                {showWinners && <div className="accordion-body">
                    <div className="accordion-list">
                        {previous_winners.map((w, i) => <AccordionItem key={i} {...w}/>)}
                    </div>
                </div>}
            </div>}
        </AsideItem>;
    }
}
